import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientVitals,
  getPatientVitals,
  getPatientVitalsCharts,
  getPatientByPatientId,
  updatePatientEthnicity,
} from "@/dispatchers/patientDispatchers";
import { getPatientActiveTreatmentPlan } from "@/dispatchers/treatmentPlanDispatchers";
interface HistoricalDataPoint {
  date: string;
  value: number;
}

export interface Vital {
  id: string;
  type: string;
  value: string;
  unit: string;
  label: string;
  description: string;
  timestamp: Date;
  historicalData: HistoricalDataPoint[];
  ethnicity?: string; // ADDED: Ethnicity field
}

/* ---------- Defaults (keep if you want fallback data) ---------- */
export const defaultVitals: Vital[] = [
  // ... your existing defaultVitals (unchanged) ...
];

/* ---------- Vital types list ---------- */
export const vitalTypes = [
  { value: "heart-rate", label: "Heart Rate", unit: "BPM" },
  { value: "blood-pressure", label: "Blood Pressure", unit: "mmHg" },
  { value: "temperature", label: "Temperature", unit: "°C" },
  { value: "oxygen-saturation", label: "Oxygen Saturation", unit: "%" }, // NEW
  { value: "respiratory-rate", label: "Respiratory Rate", unit: "breaths/min" },
  { value: "weight", label: "Weight", unit: "kg" },
  { value: "height", label: "Height", unit: "cm" },
  { value: "bmi", label: "BMI", unit: "kg/m²" },
  { value: "circumference", label: "Circumference", unit: "cm" },
  { value: "blood-sugar", label: "Blood Sugar", unit: "mmol/L" }, // NEW
  { value: "news-pain-score", label: "Pain Score", unit: "/10" }, // NEW
  { value: "consciousness-level", label: "Consciousness (AVPU)", unit: "" }, // NEW
  { value: "supplemental-oxygen", label: "Supplemental Oxygen", unit: "" }, // NEW
];

/* ---------- Helpers ---------- */
const parseDate = (iso?: string | null): Date => {
  if (!iso) return new Date();
  try {
    return new Date(iso);
  } catch {
    return new Date(iso ?? Date.now());
  }
};

const extractNumeric = (value?: string | number | null): number => {
  if (value == null) return NaN;
  const s = String(value);
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : NaN;
};

/**
 * Map UI vital.type to API vitalName expected by backend
 */
export const getVitalKey = (type: string): string => {
  switch (type) {
    case "blood-pressure":
      return "BloodPressure";
    case "heart-rate":
      return "HeartRate";
    case "temperature":
      return "Temperature";
    case "weight":
      return "Weight";
    case "oxygen-saturation":
      return "OxygenSaturation";
    case "respiratory-rate":
      return "RespiratoryRate";
    case "news-pain-score":
      return "PainScore";
    case "blood-sugar":
      return "GlucoseLevel";
    case "height":
      return "Height";
    case "circumference":
      return "Circumference";
    case "bmi":
      return "Bmi";
    case "consciousness-level":
      return "Consciousness";
    case "supplemental-oxygen":
      return "SupplementalOxygen";
    default:
      return type;
  }
};

/**
 * Convenience helper to fetch charts for a given vital type
 */
export const loadPatientVitalCharts = async (
  patientID: string,
  type: string,
) => {
  const vitalName = getVitalKey(type);
  return await getPatientVitalsCharts(patientID, vitalName);
};

/**
 * Generate 7-day historical data from a numeric currentValue.
 * If currentValue cannot be parsed, it will create a dummy flat array.
 */
export const generateHistoricalData = (
  currentValue: string,
  type: string,
): HistoricalDataPoint[] => {
  const numericValue = extractNumeric(currentValue);
  const data: HistoricalDataPoint[] = [];

  if (isNaN(numericValue)) {
    // If value not numeric — return empty or default small dataset
    for (let i = 7; i >= 1; i--) {
      data.push({ date: `Day ${8 - i}`, value: 0 });
    }
    return data;
  }

  for (let i = 7; i >= 1; i--) {
    const variance = (Math.random() - 0.5) * 0.1; // ±5% variance
    const historicalValue = Math.round(numericValue * (1 + variance) * 10) / 10;
    data.push({
      date: `Day ${8 - i}`,
      value: historicalValue,
    });
  }

  return data;
};

/* ---------- Mapper: backend patientVitals -> UI Vital[] ---------- */
export const mapPatientVitalsToDashboard = (pv: any): Vital[] => {
  if (!pv) return [];
  const out: Vital[] = [];

  const pushIf = (
    type: string,
    value: string | null | undefined,
    unit: string,
    label: string,
    description?: string,
    createdOn?: string | null,
    ethnicity?: string,
  ) => {
    if (value === null || value === undefined || value === "") return;
    const ts = parseDate(createdOn ?? pv.updatedOn ?? pv.createdOn);

    const vital: Vital = {
      id: `${type}-${ts.getTime()}`,
      type,
      value: String(value),
      unit: unit || "",
      label,
      description: description ?? `${value} ${unit || ""}`.trim(),
      timestamp: ts,
      historicalData: generateHistoricalData(String(value), type),
    };

    if (ethnicity) {
      vital.ethnicity = ethnicity;
    }

    out.push(vital);
  };

  // Heart rate
  if (pv.heartRate?.value) {
    pushIf(
      "heart-rate",
      pv.heartRate.value,
      pv.heartRate.unit || "BPM",
      "Resting Heart Rate",
      `${pv.heartRate.value} ${pv.heartRate.unit || "BPM"}`,
      pv.heartRate.createdOn,
    );
  }

  // Blood pressure (systolic/diastolic)
  if (pv.bloodPressure?.systolic?.value || pv.bloodPressure?.diastolic?.value) {
    const sys = pv.bloodPressure?.systolic?.value ?? "";
    const dia = pv.bloodPressure?.diastolic?.value ?? "";
    const createdOn =
      pv.bloodPressure?.systolic?.createdOn ??
      pv.bloodPressure?.diastolic?.createdOn ??
      pv.updatedOn;
    pushIf(
      "blood-pressure",
      `${sys}/${dia}`,
      "mmHg",
      "Blood Pressure",
      `${sys}/${dia} mmHg`,
      createdOn,
    );
  }

  // Temperature
  if (pv.temperature?.value) {
    // normalize unit to °C if backend uses 'C'
    const unit =
      pv.temperature.unit === "C" ? "°C" : pv.temperature.unit || "°C";
    pushIf(
      "temperature",
      pv.temperature.value,
      unit,
      "Body Temperature",
      `${pv.temperature.value}${unit}`,
      pv.temperature.createdOn,
    );
  }

  // BMI - UPDATED to include ethnicity
  if (pv.bmi?.value) {
    pushIf(
      "bmi",
      pv.bmi.value,
      pv.bmi.unit || "kg/m²",
      "BMI",
      `BMI ${pv.bmi.value} ${pv.bmi.unit || ""}`.trim(),
      pv.bmi.createdOn,
      pv.bmi.ethnicity,
    );
  }

  // Respiratory rate
  if (pv.respiratoryRate?.value) {
    pushIf(
      "respiratory-rate",
      pv.respiratoryRate.value,
      pv.respiratoryRate.unit || "breaths/min",
      "Respiratory Rate",
      `${pv.respiratoryRate.value} breaths/min`,
      pv.respiratoryRate.createdOn,
    );
  }

  // Blood sugar / glucose
  if (pv.glucoseLevel || pv.bloodSugar || pv.glucose) {
    const glucose = pv.glucoseLevel ?? pv.bloodSugar ?? pv.glucose;
    // if glucose is object with .value
    const value = typeof glucose === "object" ? glucose.value : glucose;
    const createdOn = glucose?.createdOn ?? pv.updatedOn;
    pushIf(
      "blood-sugar",
      value,
      (glucose?.unit && glucose.unit) || "mmol/L",
      "Blood Sugar",
      `${value} ${glucose?.unit || "mmol/L"}`,
      createdOn,
    );
  }

  // Pain / NEWS
  if (pv.painScore?.value ?? pv.painScore) {
    const raw = pv.painScore?.value ?? pv.painScore;
    pushIf(
      "news-pain-score",
      raw,
      "/10",
      "Pain Score",
      `Pain score ${raw} /10`,
      pv.painScore?.createdOn ?? pv.updatedOn,
    );
  }

  // Circumference (backend returns a string like "Waist: 80 Hip: 90 ...")
  if (pv.circumference?.value) {
    pushIf(
      "circumference",
      pv.circumference.value,
      pv.circumference.unit || "cm",
      "Circumference",
      pv.circumference.value,
      pv.circumference.createdOn,
    );
  }

  // Weight (if present)
  if (pv.weight?.value) {
    pushIf(
      "weight",
      pv.weight.value,
      pv.weight?.unit || "kg",
      "Weight",
      `${pv.weight.value} ${pv.weight?.unit || "kg"}`,
      pv.weight?.createdOn ?? pv.updatedOn,
    );
  }

  // Height
  if (pv.height?.value) {
    const unit = pv.height.unit || "cm";
    pushIf(
      "height",
      pv.height.value,
      unit,
      "Height",
      `${pv.height.value}${unit}`,
      pv.height.createdOn,
    );
  }
  if (pv.oxygenSaturation?.value) {
    pushIf(
      "oxygen-saturation",
      pv.oxygenSaturation.value,
      pv.oxygenSaturation.unit || "%",
      "Oxygen Saturation",
      `${pv.oxygenSaturation.value}%`,
      pv.oxygenSaturation.createdOn,
    );
  }

  if (pv.consciousness?.value) {
    pushIf(
      "consciousness-level",
      pv.consciousness.value,
      "",
      "Consciousness (AVPU)",
      pv.consciousness.value,
      pv.consciousness.createdOn,
    );
  }

  if (
    pv.supplementalOxygen?.value !== undefined &&
    pv.supplementalOxygen?.value !== null
  ) {
    const val = String(pv.supplementalOxygen.value);
    pushIf(
      "supplemental-oxygen",
      val === "true" || val === "Yes" ? "Yes" : "No",
      "",
      "Supplemental Oxygen",
      val === "true" || val === "Yes" ? "Yes" : "No",
      pv.supplementalOxygen.createdOn,
    );
  }

  // If nothing mapped, return empty — caller may fallback to defaultVitals
  return out;
};

/* ---------- Hook that returns DashboardPatientVitals ---------- */
export const usePatientVitalsState = () => {
  const { patientState } = useTitanApp();
  const {
    patientVitals,
    selectedPatientDetails,
    patientVitalsLoading,
    showPatientDetailsEditModal,
    patientVitalsChart,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};
  const DashboardPatientVitals: Vital[] =
    mapPatientVitalsToDashboard(patientVitals) || [];

  // AFTER
  const updateEthnicityIfRequired = async (bmi) => {
    if (bmi == null) return;

    const ethnicityVal = Number(bmi.Ethnicity);

    if (!ethnicityVal || ethnicityVal === 0) return;

    if (ethnicityVal === selectedPatientDetails?.ethnicity) return;

    const ethnicityObj = {
      patientID: selectedPatientDetails?.patientID,
      ethnicity: ethnicityVal,
    };

    await updatePatientEthnicity(ethnicityObj);
  };

  // Main function to add new patient vital
  const addNewPatientVital = async (
    newVital: any,
    patientID: string,
    patientName: string,
    // patientVitals: any,
    _dashboardVitals?: any,
  ) => {
    if (!newVital?.type) return;
    if (newVital.type === "image") return;
    const createdOnISO =
      newVital?.data?.date && String(newVital.data.date).trim() !== ""
        ? new Date(newVital.data.date).toISOString()
        : new Date().toISOString();
    const timestampISO = new Date(
      newVital.timestamp || Date.now(),
    ).toISOString();

    let obj: any = {
      PatientId: String(patientID || ""),
      PatientName: String(patientName || ""),
      CreatedOn: createdOnISO,
    };

    const parseEthnicityForBackend = (
      ethnicity: string,
    ): number | undefined => {
      if (!ethnicity || ethnicity === "") return undefined;
      const num = parseInt(ethnicity);

      return !isNaN(num) && num >= 0 && num <= 18 ? num : undefined;
    };

    const ethnicityEnum = parseEthnicityForBackend(newVital.data?.ethnicity);

    switch (newVital.type) {
      case "heart-rate":
        obj.HeartRate = {
          Value: String(newVital.data.value || ""),
          Unit: "BPM",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;

      case "weight": {
        let heightInMeters = 0;
        if (
          patientVitals?.height?.unit === "ft/in" &&
          patientVitals?.height?.value
        ) {
          const match = String(patientVitals.height.value).match(
            /(\d+)\s*ft\s*(\d*)\s*in?/i,
          );
          const feet = match ? Number(match[1]) : 0;
          const inch = match && match[2] ? Number(match[2]) : 0;
          heightInMeters = feet * 0.3048 + inch * 0.0254;
        } else {
          heightInMeters = parseFloat(patientVitals?.height?.value) / 100 || 0;
        }

        const weightInKg =
          newVital.data.unit === "kg"
            ? parseFloat(newVital.data.value)
            : parseFloat(newVital.data.value) * 0.453592;

        const weightObj: any = {
          Value: String(newVital.data.value || ""),
          Unit: String(newVital.data.unit || ""),
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };

        if (ethnicityEnum !== undefined) {
          weightObj.Ethnicity = ethnicityEnum;
        }

        obj.Weight = weightObj;

        obj = addBMI(
          obj,
          weightInKg,
          heightInMeters,
          ethnicityEnum,
          createdOnISO,
        );
        break;
      }

      case "blood-pressure":
        obj.BloodPressure = {
          Systolic: {
            Value: String(newVital.data.systolic || ""),
            Unit: "mmHg",
            CreatedOn: new Date(newVital.timestamp).toISOString(),
          },
          Diastolic: {
            Value: String(newVital.data.diastolic || ""),
            Unit: "mmHg",
            CreatedOn: new Date(newVital.timestamp).toISOString(),
          },
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;

      case "temperature":
        obj.Temperature = {
          Value: String(newVital.data.value || ""),
          Unit: String(newVital.data.unit || ""),
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;

      case "respiratory-rate":
        obj.RespiratoryRate = {
          Value: String(newVital.data.value || ""),
          Unit: "breaths/min",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;

      case "height": {
        let heightString = "";
        let heightInMeters = 0;

        if (newVital.data.unit === "cm") {
          heightString = String(newVital.data.value || "");
          heightInMeters = parseFloat(newVital.data.value) / 100;
        } else if (newVital.data.unit === "ft/in") {
          const feet = parseInt(newVital.data.feet) || 0;
          const inch = parseInt(newVital.data.inches) || 0;
          heightString = `${feet} ft ${inch} in`;
          heightInMeters = feet * 0.3048 + inch * 0.0254;
        }

        const weightInKg =
          patientVitals?.weight?.unit === "lbs"
            ? parseFloat(patientVitals?.weight?.value) * 0.453592
            : parseFloat(patientVitals?.weight?.value);

        const heightObj: any = {
          Value: heightString,
          Unit: String(newVital.data.unit || ""),
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };

        if (ethnicityEnum !== undefined) {
          heightObj.Ethnicity = ethnicityEnum;
        }

        obj.Height = heightObj;

        obj = addBMI(
          obj,
          weightInKg,
          heightInMeters,
          ethnicityEnum,
          createdOnISO,
        );
        break;
      }

      case "circumference":
        obj.CircumferenceValue = {
          Unit: String(newVital.data.unit || ""),
          Waist: String(newVital.data.waist || ""),
          Hip: String(newVital.data.hip || ""),
          Neck: String(newVital.data.neck || ""),
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        obj.Circumference = {
          CreatedOn: new Date(newVital.timestamp).toISOString(),
          Unit: String(newVital.data.unit || ""),
          Value: [
            newVital.data.waist ? `Waist: ${newVital.data.waist}` : null,
            newVital.data.hip ? `Hip: ${newVital.data.hip}` : null,
            newVital.data.neck ? `Neck: ${newVital.data.neck}` : null,
          ]
            .filter(Boolean)
            .join(" "),
        };
        break;

      case "bmi": {
        let heightInMeters = 0;
        let weightInKg = 0;

        if (newVital.data.heightUnit === "cm") {
          heightInMeters = parseFloat(newVital.data.height || "0") / 100;
        } else if (newVital.data.heightUnit === "ft/in") {
          const feet = parseInt(newVital.data.feet) || 0;
          const inches = parseInt(newVital.data.inch) || 0;
          heightInMeters = feet * 0.3048 + inches * 0.0254;
        }

        if (newVital.data.weightUnit === "kg") {
          weightInKg = parseFloat(newVital.data.weight || "0");
        } else {
          weightInKg = parseFloat(newVital.data.weight || "0") * 0.453592;
        }

        if (newVital.data.heightUnit) {
          let heightValue = "";
          if (newVital.data.heightUnit === "cm") {
            heightValue = String(newVital.data.height || "");
          } else if (newVital.data.heightUnit === "ft/in") {
            const feet = parseInt(newVital.data.feet) || 0;
            const inches = parseInt(newVital.data.inch) || 0;
            heightValue = `${feet} ft ${inches} in`;
          }

          const heightObj: any = {
            Value: heightValue,
            Unit: newVital.data.heightUnit,
            CreatedOn: timestampISO,
          };

          if (ethnicityEnum !== undefined) {
            heightObj.Ethnicity = ethnicityEnum;
          }

          obj.Height = heightObj;
        }

        if (newVital.data.weight) {
          const weightObj: any = {
            Value: String(newVital.data.weight || ""),
            Unit: String(newVital.data.weightUnit || ""),
            CreatedOn: timestampISO,
          };

          if (ethnicityEnum !== undefined) {
            weightObj.Ethnicity = ethnicityEnum;
          }

          obj.Weight = weightObj;
        }

        obj = addBMI(
          obj,
          weightInKg,
          heightInMeters,
          ethnicityEnum,
          timestampISO,
        );

        break;
      }
      case "oxygen-saturation":
        obj.OxygenSaturation = {
          Value: String(newVital.data.value || ""),
          Unit: "%",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;
      case "blood-sugar":
        obj.GlucoseLevel = {
          Value: String(newVital.data.value || ""),
          Unit: String(newVital.data.unit || "mmol/L"),
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;
      case "news-pain-score":
        obj.PainScore = {
          Value: String(newVital.data.value || ""),
          Unit: "/10",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;
      case "consciousness-level":
        obj.Consciousness = {
          Value: String(newVital.data.value || "Alert"),
          Unit: "",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;
      case "supplemental-oxygen":
        obj.SupplementalOxygen = {
          Value: String(newVital.data.value || "No"),
          Unit: "",
          CreatedOn: new Date(newVital.timestamp).toISOString(),
        };
        break;
    }
    await addPatientVitals(obj);
    await getPatientActiveTreatmentPlan(patientID);
    await updateEthnicityIfRequired(obj.Bmi);
    await getPatientVitals(patientID);
    await getPatientByPatientId(patientID);
  };

  /**
   * Helper: calculate BMI and attach to payload with proper C# property names
   */
  const addBMI = (
    obj: any,
    weightInKg: number,
    heightInMeters: number,
    ethnicity?: number,
    createdOn?: string,
  ) => {
    if (heightInMeters > 0 && weightInKg > 0) {
      const bmi = weightInKg / (heightInMeters * heightInMeters);
      const bmiObj: any = {
        Value: bmi.toFixed(2),
        Unit: "kg/m²",
      };

      if (createdOn) {
        bmiObj.CreatedOn = createdOn;
      }

      if (ethnicity !== undefined && ethnicity !== null) {
        bmiObj.Ethnicity = ethnicity;
      } else {
      }

      obj.Bmi = bmiObj;
    }
    return obj;
  };

  return {
    patientVitals,
    DashboardPatientVitals,
    patientID,
    patientVitalsLoading,
    selectedPatientDetails,
    showPatientDetailsEditModal,
    patientVitalsChart,
    updateEthnicityIfRequired,
    addNewPatientVital,
    defaultVitals,
    generateHistoricalData,
  };
};
